import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  CloseButton,
  Button,
  NativeSelect,
  NumberInput,
  Checkbox,
  Slider,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Collapsible,
  Spinner,
  Field,
  Dialog,
  Portal,
  type NumberInputValueChangeDetails,
  type SliderValueChangeDetails,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { useContactLists } from '../../hooks/useContactList'
import { useAutoGenerateTurfMap } from '../../hooks/useTurfMap'
import { useJobPolling } from '../../hooks/useJobPolling'
import type { WalkType } from '../../types/turf'

interface CampaignAutoGenerateModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  campaignLabel: string
  onSuccess?: () => void
}

export function CampaignAutoGenerateModal({
  isOpen,
  onClose,
  campaignId,
  campaignLabel,
  onSuccess,
}: CampaignAutoGenerateModalProps) {
  const queryClient = useQueryClient()
  const { data: allLists, isLoading: listsLoading } = useContactLists()
  const autoGenerate = useAutoGenerateTurfMap()

  // Filter to standalone lists only
  const standaloneLists = allLists?.filter(l => !(l as any).campaignId)

  const [selectedListId, setSelectedListId] = useState('')
  const [targetTurfCount, setTargetTurfCount] = useState(10)
  const [walkType, setWalkType] = useState<WalkType>('Walk')
  const [refineWithOsrm, setRefineWithOsrm] = useState(true)
  const [concaveHullRatio, setConcaveHullRatio] = useState(0.7)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)

  const { data: job, isTerminal } = useJobPolling(jobId)
  const isGenerating = autoGenerate.isPending || (!!jobId && !isTerminal)

  const canSubmit = selectedListId !== '' && targetTurfCount >= 1

  const handleClose = () => {
    if (isGenerating) return
    setSelectedListId('')
    setTargetTurfCount(10)
    setWalkType('Walk')
    setRefineWithOsrm(true)
    setConcaveHullRatio(0.7)
    setShowAdvanced(false)
    onClose()
  }

  useEffect(() => {
    if (!isTerminal) return

    if (job?.status === 'Succeeded') {
      // Invalidate here (job actually finished), not on the enqueue mutation's onSuccess —
      // that fires at 202-accept, before the turf map exists / hasTurfMap flips, and would
      // just refetch stale pre-job data (worse: it resets the 5-minute staleTime clock to
      // the wrong moment, leaving the campaign checklist stale for up to 5 minutes).
      queryClient.invalidateQueries({ queryKey: ['turf-maps'] })
      queryClient.invalidateQueries({ queryKey: ['turfs'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] }) // hasTurfMap flag
      toaster.create({ title: 'Turfs generated', type: 'success', duration: 5000 })
      setJobId(null)
      handleClose()
      onSuccess?.()
    } else {
      // Job-level Failed, or the poll itself terminated in error (e.g. 404) — either way,
      // surface it and let the user retry rather than leaving the spinner running forever.
      toaster.create({
        title: 'Auto-turf failed',
        description: job?.errorMessage ?? 'An error occurred.',
        type: 'error',
        duration: 7000,
      })
      setJobId(null) // allow retry
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerminal, job])

  const handleSubmit = async () => {
    if (!canSubmit) return

    try {
      const { jobId: newJobId } = await autoGenerate.mutateAsync({
        turfMapLabel: '',
        sourceContactListId: selectedListId,
        campaignId,
        targetTurfCount,
        walkType,
        refineWithOsrm,
        concaveHullRatio,
      })

      setJobId(newJobId)
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message || 'Auto-generation failed. Please try again.'
      toaster.create({
        title: 'Generation failed',
        description: message,
        type: 'error',
        duration: 7000,
      })
    }
  }

  return (
    <Dialog.Root open={isOpen} size='lg' closeOnInteractOutside={!isGenerating} onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Auto-Generate Turfs</Dialog.Header>
            {!isGenerating && <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>}
            <Dialog.Body>
              {isGenerating ? (
                <VStack py={12} gap={4}>
                  <Spinner size="xl" color="accent" borderWidth="4px" />
                  <Text color="gray.600" fontWeight="medium">Generating turfs...</Text>
                  <Text color="gray.400" fontSize="sm">
                    This may take a minute for large lists.
                  </Text>
                </VStack>
              ) : (
                <VStack gap={4} align="stretch">
                  <Box bg="info.subtle" p={3} borderRadius="md">
                    <HStack>
                      <Text fontSize="sm" color="info.fg">Campaign:</Text>
                      <Badge bg="info.subtle" color="info.fg">{campaignLabel}</Badge>
                    </HStack>
                  </Box>

                  <Field.Root required disabled={listsLoading}>
                    <Field.Label>Source Contact List</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        placeholder={listsLoading ? 'Loading lists...' : 'Select a contact list'}
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}>
                        {standaloneLists?.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.label} ({list.memberCount?.toLocaleString() ?? '?'} members)
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Field.HelperText>Only geocoded contacts will be included.</Field.HelperText>
                  </Field.Root>

                  <HStack gap={4}>
                    <Field.Root required flex={1}>
                      <Field.Label>Number of Turfs</Field.Label>
                      <NumberInput.Root
                        size="sm"
                        min={1}
                        max={100}
                        value={String(targetTurfCount)}
                        onValueChange={(details: NumberInputValueChangeDetails) => setTargetTurfCount(details.valueAsNumber || 1)}
                      >
                        <NumberInput.Input />
                        <NumberInput.Control>
                          <NumberInput.IncrementTrigger />
                          <NumberInput.DecrementTrigger />
                        </NumberInput.Control>
                      </NumberInput.Root>
                    </Field.Root>

                    <Field.Root flex={1}>
                      <Field.Label>Walk Type</Field.Label>
                      <NativeSelect.Root size="sm">
                        <NativeSelect.Field
                          value={walkType}
                          onChange={(e) => setWalkType(e.target.value as WalkType)}>
                          <option value="Walk">Walk</option>
                          <option value="Drive">Drive</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                  </HStack>

                  <Checkbox.Root
                    onCheckedChange={(e) => setRefineWithOsrm(e.checked === true)}
                    size="sm"
                    checked={refineWithOsrm}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                    <Text fontSize="sm">Refine with road-network distances (requires OSRM)</Text>
                  </Checkbox.Label></Checkbox.Root>

                  <Box>
                    <Button variant="ghost" size="xs" onClick={() => setShowAdvanced(!showAdvanced)}>Advanced Options
                                      {showAdvanced ? <FiChevronUp /> : <FiChevronDown />}</Button>
                    <Collapsible.Root open={showAdvanced}>
                      <Collapsible.Content>
                        <Box mt={2} p={3} bg="gray.50" borderRadius="md">
                          <Field.Root>
                            <Field.Label fontSize="sm">Boundary Tightness</Field.Label>
                            <HStack gap={3}>
                              <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Loose</Text>
                              <Slider.Root
                                min={0}
                                max={1}
                                step={0.1}
                                value={[concaveHullRatio]}
                                onValueChange={(details: SliderValueChangeDetails) => setConcaveHullRatio(details.value[0])}
                              >
                                <Slider.Control>
                                  <Slider.Track>
                                    <Slider.Range />
                                  </Slider.Track>
                                  <Slider.Thumb index={0}>
                                    <Slider.HiddenInput />
                                  </Slider.Thumb>
                                </Slider.Control>
                              </Slider.Root>
                              <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Tight</Text>
                            </HStack>
                            <Field.HelperText fontSize="xs">
                              Controls how tightly the turf boundary wraps around contacts.
                            </Field.HelperText>
                          </Field.Root>
                        </Box>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </Box>
                </VStack>
              )}
            </Dialog.Body>
            {!isGenerating && (
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  colorPalette="brand"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  Generate Turfs
                </Button>
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
